import { useState, useRef, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import AnnotationCanvas from './AnnotationCanvas';
import type { Annotation } from '../../api/client';
import type { AnnotationTool } from './AnnotationToolbar';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2 } from 'lucide-react';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PDFViewerProps {
  url: string;
  annotations: Annotation[];
  onAnnotationCreate: (annotation: {
    annotationType: Annotation['AnnotationType'];
    coordinates: any;
    pageNumber?: number | null;
    content?: string;
    color?: string;
  }) => void;
  onAnnotationDelete: (annotationId: string) => void;
  activeTool: AnnotationTool;
  activeColor: string;
  rectangleFill?: boolean;
  readOnly?: boolean;
}

export default function PDFViewer({
  url,
  annotations,
  onAnnotationCreate,
  onAnnotationDelete,
  activeTool,
  activeColor,
  rectangleFill = false,
  readOnly = false,
}: PDFViewerProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [pageSize, setPageSize] = useState({ width: 0, height: 0 });
  const [loading, setLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);

  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setLoading(false);
  }, []);

  const onPageLoadSuccess = useCallback(({ width, height }: { width: number; height: number }) => {
    setPageSize({ width, height });
  }, []);

  // Fit to container width
  useEffect(() => {
    if (containerRef.current && pageSize.width > 0) {
      const containerWidth = containerRef.current.clientWidth - 32; // padding
      const fitScale = containerWidth / pageSize.width;
      if (Math.abs(fitScale - scale) > 0.1 && scale === 1.0) {
        setScale(Math.min(fitScale, 1.5));
      }
    }
  }, [pageSize.width]);

  const scaledWidth = pageSize.width * scale;
  const scaledHeight = pageSize.height * scale;

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center justify-between bg-gray-50 border-b border-gray-200 px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} / {numPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(numPages, currentPage + 1))}
            disabled={currentPage >= numPages}
            className="p-1 rounded hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setScale(Math.max(0.5, scale - 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom out"
          >
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => setScale(Math.min(2.5, scale + 0.25))}
            className="p-1 rounded hover:bg-gray-200"
            title="Zoom in"
          >
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-100 p-4">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading PDF...</span>
          </div>
        )}
        <div className="flex justify-center">
          <div className="relative" style={{ width: scaledWidth || 'auto' }}>
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(err) => { console.error('PDF load error:', err); setLoading(false); }}
              loading={null}
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                onLoadSuccess={onPageLoadSuccess}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>

            {/* Annotation overlay */}
            {scaledWidth > 0 && scaledHeight > 0 && (
              <AnnotationCanvas
                annotations={annotations}
                onAnnotationCreate={onAnnotationCreate}
                onAnnotationDelete={onAnnotationDelete}
                activeTool={activeTool}
                activeColor={activeColor}
                pageNumber={currentPage}
                width={scaledWidth}
                height={scaledHeight}
                readOnly={readOnly}
                rectangleFill={rectangleFill}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
